import {match, Pattern} from 'ts-pattern';

import {useCallback, useEffect, useRef} from 'react';
import {
  NavigateOptions,
  // eslint-disable-next-line no-restricted-imports
  useNavigate as useReactRouterNavigate,
  To,
  createPath,
  NavigateFunction,
} from 'react-router-dom';

import {includes} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

const DG_ROW_CLICK_DELAY = 400;
const PATH_PATTERN = {
  pathname: Pattern.optional(Pattern.string),
  search: Pattern.optional(Pattern.string),
  hash: Pattern.optional(Pattern.string),
};
/**
 * A hook that returns a function to navigate to a given path.
 *
 * @returns {function(To | number, NavigateOptions?): void} A navigation function that accepts a path or delta and optional navigation options.
 *
 * @description
 * This hook extends React Router's useNavigate with additional functionality:
 * - If the control or command key is pressed when the function is called, the path will be opened in a new tab.
 * - When the options parameter is provided, or the path parameter is a number, the returned function will always open the path in the same tab.
 *
 * @example
 * const navigate = useNavigate();
 *
 * // Navigate to a new path in the same tab
 * navigate('/some/path');
 *
 * // Navigate to a new path with options
 * navigate('/some/path', { replace: true });
 *
 * // Navigate back in history
 * navigate(-1);
 */
export const useNavigate = () => {
  const navigate = useReactRouterNavigate();

  const isControlOrCommandPressed = useRef<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (includes(event.key, ['Control', 'Meta'])) {
        isControlOrCommandPressed.current = true;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) =>
      // We need to have a timeout here to compensate for the row click delay in DataGrid
      setTimeout(() => {
        if (includes(event.key, ['Control', 'Meta'])) {
          isControlOrCommandPressed.current = false;
        }
      }, DG_ROW_CLICK_DELAY);
    const handleBlur = () => (isControlOrCommandPressed.current = false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  return useCallback<NavigateFunction>(
    (toOrDelta: To | number, options?: NavigateOptions) => {
      match([toOrDelta, isControlOrCommandPressed.current && isNilOrEmpty(options)])
        .with([Pattern.number, Pattern._], ([delta]) => {
          navigate(delta as number);
        })
        .with([Pattern.string, false], ([to]) => {
          navigate(to, options);
        })
        .with([Pattern.string, true], ([to]) => {
          window.open(to);
        })
        .with([PATH_PATTERN, true], ([to]) => {
          window.open(createPath(to));
        })
        .with([PATH_PATTERN, false], ([to]) => {
          navigate(createPath(to), options);
        });
    },
    [navigate]
  );
};
